import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { GiConfirmed } from 'react-icons/gi';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useServiceActions from '../../../hooks/APIHooks/appointments/useServiceActions';
import useUserExpertiseActions from '../../../hooks/APIHooks/users/useUserExpertiseActions';
import { SelectData } from '../../../types/common';
import { Expertise, ExpertiseReqBody } from '../../../types/expertise';
import { User } from '../../../types/users';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const UpdateServiceExpertiseModal = ({
  expertiseList,
  handleModalClose,
}: {
  expertiseList: Array<Expertise>;
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [selectedServices, setSelectedServices] = useState<Array<SelectData>>(
    []
  );
  const [resetSelectField, setResetSelectField] = useState<boolean>(false);

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { getServiceListHandler } = useServiceActions('serviceList');
  const { updateUserExpertiseHandler } = useUserExpertiseActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // setting already added services to list here
  useEffect(() => {
    if (expertiseList.length) {
      setSelectedServices(
        expertiseList.map((expertise) => ({
          id: expertise.service_id,
          name: expertise.service_name,
          code: expertise.id.toString(),
        }))
      );
    }
  }, [expertiseList]);

  const serviceOptions = useMemo(() => {
    if (!getServiceListHandler.data?.is_success) return [];

    return getServiceListHandler.data?.data
      .filter(
        (service) =>
          !selectedServices.some(
            (selectedService) => selectedService.id === service.id
          )
      )
      .map((service) => ({
        id: service.id,
        name: service.name,
      }));
  }, [getServiceListHandler.data, selectedServices]);

  // handler function to handle updating service expertise declared here
  const updateServiceExpertiseHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      const body: ExpertiseReqBody = selectedServices.map((service) =>
        service.code
          ? {
              id: +service.code,
              user: currentUser?.id as number,
              service_id: service.id,
              updated_by: currentUser?.username,
            }
          : {
              user: currentUser?.id as number,
              service_id: service.id,
              created_by: currentUser?.username,
            }
      );

      await updateUserExpertiseHandler
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Service Expertise Updated!!');

            handleModalClose();
            queryClient.invalidateQueries({
              queryKey: [
                'user-profile-by-id',
                (currentUser as User).user_profile_id,
              ],
            });
          } else {
            toast.error(res.message || 'Expertise Updating Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // handler function to remove selected service from the list declared here
  const removeSelectedServiceHandler = (serviceId: number) => {
    setSelectedServices((state) =>
      state.filter((service) => service.id !== serviceId)
    );
  };

  // rendering update provided service modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='service expertise' />

      <Formik
        initialValues={{ service: SELECT_FIELD_DEFAULT_VALUE }}
        onSubmit={updateServiceExpertiseHandler}
      >
        {(props) => (
          <Form>
            <div className='mt-5 space-y-5'>
              <AcademiaFormSelect
                options={serviceOptions}
                name='service'
                label='Select Service'
                showSearchInput
                defaultValue={SELECT_FIELD_DEFAULT_VALUE}
                setValueOnChange={(service) => {
                  setSelectedServices((state) => [...state, service]);
                  setResetSelectField(true);
                }}
                resetSelectField={resetSelectField}
                setResetSelectField={() => setResetSelectField(false)}
                isRequired
              />

              {selectedServices.length ? (
                <div className='flex flex-wrap items-center gap-3 border-2 p-3 rounded-lg border-dashed border-legal-dark-blue'>
                  {selectedServices.map((service) => (
                    <SkillBadge
                      key={service.id}
                      badgeLabel={service.name}
                      color='#845ADF'
                      onClickRemove={() =>
                        removeSelectedServiceHandler(service.id)
                      }
                      isEditing
                    />
                  ))}
                </div>
              ) : null}

              <AcademiaButton
                type='submit'
                variant='premium'
                icon={<GiConfirmed />}
                isLoading={props.isSubmitting}
                fullWidth
              >
                Confirm
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default UpdateServiceExpertiseModal;
